
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  type?: string;
  name?: string;
  imageUrl?: string;
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  category?: string;
  keywords?: string[];
  isBlogPost?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'CIPHER Security | Prevent, Detect, Respond',
  description = 'CIPHER Security provides comprehensive Cybersecurity Services including Penetration Testing, DevSecOps, Application Security, and Compliance solutions for enterprises.',
  type = 'website',
  name = 'CIPHER Security',
  imageUrl = '/images/cipher_character_notext.png',
  publishDate,
  modifiedDate,
  author,
  category,
  keywords = [
    'CIPHER Security',
    'Cybersecurity Services',
    'Penetration Testing',
    'DevSecOps Consulting',
    'Application Security',
    'Cloud Security',
    'Vulnerability Assessment',
    'Security Compliance',
    'Incident Response',
    'Threat Intelligence',
    'Cybersecurity Consulting',
    'Enterprise Security',
    'Security Audit',
    'SAST/DAST',
    'Security Training',
    'Risk Management',
    'Cybersecurity Company'
  ],
  isBlogPost = false
}) => {
  const location = useLocation();
  
  // Normalize URL by removing trailing slashes and ensuring consistency
  const normalizedPathname = location.pathname.endsWith('/') && location.pathname !== '/' 
    ? location.pathname.slice(0, -1) 
    : location.pathname;
  
  const currentUrl = `https://ciphersecurity.io${normalizedPathname}`;
  const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : `https://ciphersecurity.io${imageUrl}`;

  // Create base Organization JSON-LD structured data
  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'LocalBusiness'],
    name: 'CIPHER Security',
    alternateName: 'CIPHERSecurity.io',
    url: 'https://ciphersecurity.io',
    logo: {
      '@type': 'ImageObject',
      url: 'https://ciphersecurity.io/images/cipher_character_notext.png',
      width: '512',
      height: '512'
    },
    description: 'CIPHER Security provides comprehensive Cybersecurity Services including Penetration Testing, DevSecOps, Application Security, Cloud Security, and Compliance solutions for enterprises worldwide.',
    foundingDate: '2024',
    industry: 'Cybersecurity',
    knowsAbout: [
      'Cybersecurity',
      'Penetration Testing',
      'DevSecOps',
      'Application Security',
      'Cloud Security',
      'Vulnerability Assessment',
      'Incident Response',
      'Compliance Auditing',
      'Threat Intelligence',
      'Security Training'
    ],
    serviceArea: {
      '@type': 'Place',
      name: 'Global'
    },
    areaServed: [
      {
        '@type': 'Country',
        name: 'United States'
      },
      {
        '@type': 'Country', 
        name: 'Canada'
      },
      {
        '@type': 'Country',
        name: 'United Kingdom'
      },
      {
        '@type': 'Country',
        name: 'Australia'
      }
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'sales@ciphersecurity.io',
        telephone: '+1-848-339-1572',
        availableLanguage: 'English'
      },
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: 'sales@ciphersecurity.io',
        telephone: '+1-848-339-1572',
        availableLanguage: 'English'
      }
    ],
    sameAs: [
      'https://www.linkedin.com/company/ciphersecurity-io',
      'https://twitter.com/ciphersecurityio'
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Cybersecurity Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Penetration Testing Services',
            description: 'Comprehensive penetration testing for web applications, networks, and cloud infrastructure'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'DevSecOps Consulting',
            description: 'CI/CD pipeline security integration and DevSecOps implementation services'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Application Security Testing',
            description: 'SAST, DAST, and SCA security testing for applications'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Cloud Security Assessment',
            description: 'Cloud security posture management and compliance auditing'
          }
        }
      ]
    }
  };

  // Create BlogPosting JSON-LD structured data if it's a blog post
  const blogPostStructuredData = isBlogPost && publishDate ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl
    },
    headline: title,
    image: absoluteImageUrl,
    datePublished: publishDate,
    dateModified: modifiedDate || publishDate,
    author: {
      '@type': 'Organization',
      name: author || 'CIPHER Security'
    },
    publisher: {
      '@type': 'Organization',
      name: 'CIPHER Security',
      logo: {
        '@type': 'ImageObject',
        url: 'https://ciphersecurity.io/images/cipher_character_notext.png'
      }
    },
    description: description,
    keywords: keywords.join(', ')
  } : null;

  // Combine keywords with any additional category terms
  const keywordString = category 
    ? [...keywords, category.toLowerCase()].join(', ') 
    : keywords.join(', ');

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl} />
      <meta name="keywords" content={keywordString} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={isBlogPost ? 'article' : type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {isBlogPost && category && <meta property="article:section" content={category} />}
      {isBlogPost && publishDate && <meta property="article:published_time" content={publishDate} />}
      {isBlogPost && modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      
      {/* LinkedIn specific */}
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta name="author" content={author || name} />
      
      {/* Pinterest specific */}
      <meta name="pinterest:description" content={description} />
      <meta name="pinterest:image" content={absoluteImageUrl} />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
      
      {blogPostStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(blogPostStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
